import { Button } from '@bambi/component-library/src/components/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';
import { ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { Fragment, useCallback, useState } from 'react';

import { NotificationItem } from './NotificationItem';
import { notificationListRenderers } from './renderers/renderers';
import { useAppDispatch } from 'src/app/store/hooks';
import {
  useNotificationsNotificationsListQuery,
  useNotificationsNotificationsMarkAllReadPartialUpdateMutation,
} from 'src/common/external/bambi-api/notificationsApi';
import { EmptyState } from 'src/common/primitives/EmptyState/EmptyState';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

export function NotificationList() {
  const dispatch = useAppDispatch();
  const [showMore, setShowMore] = useState(false);
  const { data, isLoading } = useNotificationsNotificationsListQuery({
    pageSize: showMore ? 100 : 10,
    isRead: false,
  });

  const [trigger] =
    useNotificationsNotificationsMarkAllReadPartialUpdateMutation();

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await trigger().unwrap();
      dispatch(
        show({
          type: 'success',
          title: 'Successfully marked all notifications as read',
        })
      );
    } catch (e) {
      dispatch(
        show({
          type: 'error',
          title: 'Failed to mark all notifications as read',
          description: formatServerError(e),
        })
      );
    }
  }, [trigger, dispatch]);

  const showLoadingState = isLoading;
  const showEmptyState = (data?.results.length ?? 0) === 0;
  const hasMore = Boolean(data?.next);

  return (
    <div className="flex min-w-[400px] flex-col gap-1">
      {showLoadingState ? (
        <div className="flex flex-row justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          {!showEmptyState && (
            <div className="flex max-h-96 flex-col gap-1 overflow-auto">
              {data?.results.map((notification) => {
                const renderItem =
                  notificationListRenderers[
                    notification.notification_type_name
                  ];

                if (!renderItem) {
                  console.warn(
                    'Missing component for notification_type',
                    notification.notification_type_name
                  );
                  return null;
                }

                const { Component, getMetaData } = renderItem;
                const metaData = getMetaData(notification);

                return (
                  <Fragment key={notification.id}>
                    <div
                      className={`border-l-2 pl-2 ${notification.read_at ? 'border-l-transparent' : 'border-l-mint'}`}
                    >
                      <NotificationItem
                        title={metaData.title}
                        notification={notification}
                        Renderer={Component}
                      />
                    </div>
                    <div className="border-b" />
                  </Fragment>
                );
              })}
            </div>
          )}
          {hasMore && !showMore ? (
            <Button variant="ghost" onClick={() => setShowMore(true)}>
              View More
            </Button>
          ) : null}
          {showEmptyState && (
            <EmptyState
              entityName="Notifications"
              description=""
              Icon={<ChatBubbleLeftEllipsisIcon className="w-6" />}
              Title="No unread notifications found"
            />
          )}
        </>
      )}
      {!showEmptyState && !showLoadingState ? (
        <div className="mt-4 flex flex-row justify-end text-sm">
          <Button variant="primary" onClick={handleMarkAllAsRead}>
            Mark all as read
          </Button>
        </div>
      ) : null}
    </div>
  );
}
